import React from 'react';
import { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { X } from 'lucide-react';
import { Frequency, Schedule, IncomeStream, RecurringExpense, BankAccount } from '../types';

interface BudgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  householdId: string;
  editingIncome?: IncomeStream | null;
  editingExpense?: RecurringExpense | null;
  bankAccounts?: BankAccount[];
}

export default function BudgetModal({ isOpen, onClose, userId, householdId, editingIncome, editingExpense, bankAccounts = [] }: BudgetModalProps) {
  const [type, setType] = useState<'income' | 'expense'>('income');
  const [name, setName] = useState('');
  const [expenseName, setExpenseName] = useState('');
  const [notes, setNotes] = useState('');
  const [amount, setAmount] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  
  const [frequency, setFrequency] = useState<Frequency>('monthly');
  const [date1, setDate1] = useState('1');
  const [date2, setDate2] = useState('15');
  const [nextDate, setNextDate] = useState(() => new Date().toISOString().split('T')[0]);
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingIncome && isOpen) {
      setType('income');
      setName(editingIncome.source);
      setAmount(editingIncome.amount.toString());
      setFrequency(editingIncome.schedule.frequency);
      if (editingIncome.schedule.date1) setDate1(editingIncome.schedule.date1.toString());
      if (editingIncome.schedule.date2) setDate2(editingIncome.schedule.date2.toString());
      if (editingIncome.schedule.nextDate) {
        try { setNextDate(editingIncome.schedule.nextDate.toISOString().split('T')[0]); } catch(e) {}
      }
      setBankAccountId(editingIncome.bankAccountId || '');
    } else if (editingExpense && isOpen) {
      setType('expense');
      setName(editingExpense.category);
      setExpenseName(editingExpense.name || '');
      setNotes(editingExpense.notes || '');
      setAmount(editingExpense.amount.toString());
      setFrequency(editingExpense.schedule.frequency);
      if (editingExpense.schedule.date1) setDate1(editingExpense.schedule.date1.toString());
      if (editingExpense.schedule.date2) setDate2(editingExpense.schedule.date2.toString());
      if (editingExpense.schedule.nextDate) {
        try { setNextDate(editingExpense.schedule.nextDate.toISOString().split('T')[0]); } catch(e) {}
      }
    } else if (isOpen) {
      setType('income');
      setName('');
      setExpenseName('');
      setNotes('');
      setAmount('');
      setFrequency('monthly');
      setDate1('1');
      setDate2('15');
      setNextDate(new Date().toISOString().split('T')[0]);
      setBankAccountId('');
    }
  }, [editingIncome, editingExpense, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !amount) return;
    
    setLoading(true);
    try {
      const schedule: Schedule = { frequency };
      if (frequency === 'monthly') {
        schedule.date1 = Number(date1) || 1;
      } else if (frequency === 'semi-monthly') {
        schedule.date1 = Number(date1) || 1;
        schedule.date2 = Number(date2) || 15;
      } else {
        const [year, month, day] = nextDate.split('-');
        schedule.nextDate = new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0);
      }

      if (type === 'income') {
        const incomeData: any = {
          userId,
          householdId,
          source: name,
          amount: Number(amount),
          schedule,
        };
        if (bankAccountId) {
          incomeData.bankAccountId = bankAccountId;
        } else {
          incomeData.bankAccountId = null;
        }

        if (editingIncome?.id) {
          await updateDoc(doc(db, 'incomes', editingIncome.id), incomeData);
        } else {
          await addDoc(collection(db, 'incomes'), { ...incomeData, createdAt: serverTimestamp() });
        }
      } else {
        const expenseData = {
          userId,
          householdId,
          category: name,
          name: expenseName,
          notes,
          amount: Number(amount),
          schedule
        };
        if (editingExpense?.id) {
          await updateDoc(doc(db, 'recurring_expenses', editingExpense.id), expenseData);
        } else {
          await addDoc(collection(db, 'recurring_expenses'), { ...expenseData, createdAt: serverTimestamp() });
        }
      }
      
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 relative my-auto max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-xl font-bold mb-6 text-slate-900">{(editingIncome || editingExpense) ? 'Edit Budget Item' : 'Add Budget Item'}</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex gap-4 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value="income"
                checked={type === 'income'}
                onChange={() => setType('income')}
                className="text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm font-medium text-slate-700">Income</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value="expense"
                checked={type === 'expense'}
                onChange={() => setType('expense')}
                className="text-rose-600 focus:ring-rose-500"
              />
              <span className="text-sm font-medium text-slate-700">Fixed Expense</span>
            </label>
          </div>

          {type === 'expense' && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                Expense Name
              </label>
              <input
                type="text"
                value={expenseName}
                onChange={(e) => setExpenseName(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="e.g. Netflix, Car Loan, Internet"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
              {type === 'income' ? 'Income Source' : 'Expense Category'}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder={type === 'income' ? "e.g. Salary, Side Hustle" : "e.g. Utilities, Housing, Subscriptions"}
              required
            />
          </div>

          {type === 'income' && bankAccounts.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                Deposit To Account (Optional)
              </label>
              <select
                value={bankAccountId}
                onChange={(e) => setBankAccountId(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">No specific account</option>
                {bankAccounts.map(b => (
                  <option key={b.id} value={b.id}>{b.name} (${b.currentBalance?.toFixed(2) || '0.00'})</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
              Amount (Per Pay Period/Bill)
            </label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="0.00"
              required
            />
          </div>

          {type === 'expense' && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                Notes (Optional)
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="e.g. additional principal payment"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Schedule / Frequency</label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as Frequency)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm mb-3"
            >
              <option value="monthly">Monthly (Same day every month)</option>
              <option value="semi-monthly">Semi-Monthly (Twice a month, e.g. 1st & 15th)</option>
              <option value="bi-weekly">Bi-weekly (Every 14 days)</option>
              <option value="weekly">Weekly (Every 7 days)</option>
            </select>

            {frequency === 'monthly' && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Day of month:</span>
                <input
                  type="number" min="1" max="31"
                  value={date1}
                  onChange={(e) => setDate1(e.target.value)}
                  className="w-20 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm"
                  required
                />
              </div>
            )}

            {frequency === 'semi-monthly' && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Day 1:</span>
                  <input
                    type="number" min="1" max="31"
                    value={date1}
                    onChange={(e) => setDate1(e.target.value)}
                    className="w-16 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm"
                    required
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Day 2:</span>
                  <input
                    type="number" min="1" max="31"
                    value={date2}
                    onChange={(e) => setDate2(e.target.value)}
                    className="w-16 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm"
                    required
                  />
                </div>
              </div>
            )}

            {(frequency === 'bi-weekly' || frequency === 'weekly') && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Next Expected Date:</span>
                <input
                  type="date"
                  value={nextDate}
                  onChange={(e) => setNextDate(e.target.value)}
                  className="flex-1 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm"
                  required
                />
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2.5 text-white rounded-md font-semibold text-sm disabled:opacity-50 mt-4 shadow-sm transition-colors ${
              type === 'income' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'
            }`}
          >
            {loading ? 'Saving...' : ((editingIncome || editingExpense) ? 'Save Changes' : `Add ${type === 'income' ? 'Income' : 'Expense'}`)}
          </button>
        </form>
      </div>
    </div>
  );
}
